import { SbefGroupCode, type SbefCode } from './sbef.model';

/**
 * One BSAB 96 key’s SBEF candidates, plus an optional manual pick.
 *
 * When {@link resolve} is set it wins. Otherwise lookup uses variant A: all
 * candidates in one SBEF group → that group; mixed groups → try a parent key.
 */
export type Bsab96SbefEntry = {
  codes: readonly SbefCode[];
  resolve?: SbefCode;
};

export type Bsab96ToSbefMap = Readonly<Record<string, Bsab96SbefEntry>>;

const codes = (...values: SbefCode[]): Bsab96SbefEntry => ({ codes: values });

const resolved = (
  resolve: SbefCode,
  ...values: SbefCode[]
): Bsab96SbefEntry => ({ codes: values, resolve });

/**
 * BSAB 96 code → candidate {@link SbefCode}s.
 *
 * Inverted from the Airtable SBEF/BSAB 83 ↔ BSAB 96 table (DEV-3446). Keys are
 * uppercase (`15.SG`, `27.B`).
 */
export const bsab96ToSbefCodes: Bsab96ToSbefMap = {
  '5': codes(SbefGroupCode.Installations),
  '6': codes(SbefGroupCode.Installations),
  '7': codes(SbefGroupCode.Installations),
  '8': codes(SbefGroupCode.Installations),
  '13': resolved('23', '22', '23'),
  '15': resolved('24', SbefGroupCode.HouseSubstructure, '26'),
  '15.S': resolved('24', '20', '24'),
  '15.SG': codes('27'),
  '15.SU': codes('28'),
  '15.SZ': resolved('29', '25', '29'),
  '27': codes(SbefGroupCode.Framework),
  '27.A': resolved('30', '30', '33'),
  '27.B': codes('31'),
  '27.C': codes('31'),
  '27.D': codes('32'),
  '27.E': codes('34'),
  '27.F': codes('34'),
  '27.G': resolved('41', '37', '41'),
  '27.H': resolved('38', '35', '36', '38'),
  '27.Z': resolved('39', '35', '39'),
  '40': codes('60'),
  '41': codes(SbefGroupCode.OuterRoof),
  '41.A': resolved('40', '40', '42'),
  '41.C': codes('43'),
  '41.F': resolved('46', '44', '45', '46'),
  '41.Z': resolved('49', '47', '49'),
  '42': resolved('50', SbefGroupCode.Facades, '50'),
  '42.B': resolved('53', '51', '53'),
  '42.C': codes('61'),
  '42.D': codes('55'),
  '42.E': codes('58'),
  '42.Z': codes('59'),
  '43': codes(SbefGroupCode.FrameCompletionRoomForm),
  '43.C': resolved('63', '63', '65'),
  '43.D': codes('62'),
  '43.E': codes('64'),
  '43.Z': resolved('69', '66', '69'),
  '44': codes(SbefGroupCode.InteriorSurfaceLayersRoomCompletion),
  '44.B': resolved('72', '72', '75'),
  '44.C': codes('73'),
  '44.D': codes('74'),
  '44.Z': resolved('70', '70', '76'),
  '45.B': codes('48'),
  '45.C': resolved('68', '68', '77', '78'),
  '45.Z': codes('79'),
  '50': codes('80'),
  '51': codes('84'),
  '52': resolved('84', '82', '84'),
  '55': codes('85'),
  '56': codes('84'),
  '57': codes('85'),
  '63': resolved('86', '81', '86'),
  '64': resolved('86', '86', '88'),
  '71': codes('87'),
  '73': codes('87'),
  '76': codes('87'),
  '78': codes('87'),
  '81': codes('88'),
};
